import type {
	KorrekturStudentKlausurDTO,
	MusterloesungDTO,
	KorrekturKorrekturbogenItem,
} from "@/features/korrektur/korrektur.types";
import type { NavigationKlausurDTO } from "@/types/klausurEditor.types";

/**
 * Helper functions for updating klausur state in an immutable way.
 * These utilities encapsulate common update patterns to reduce code duplication.
 */

/**
 * Updates the title of an aufgabenstellung in the detailed klausur data.
 */
export function updateAufgabenstellungTitleInData(
	data: KorrekturStudentKlausurDTO,
	aufgabenstellungId: string,
	title: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) =>
			a.id === aufgabenstellungId ? { ...a, title } : a,
		),
	};
}

/**
 * Updates the title of an aufgabenstellung in the navigation structure.
 */
export function updateAufgabenstellungTitleInNav(
	nav: NavigationKlausurDTO,
	aufgabenstellungId: string,
	title: string,
): NavigationKlausurDTO {
	return {
		...nav,
		aufgabenstellungen: nav.aufgabenstellungen.map((a) =>
			a.id === aufgabenstellungId ? { ...a, title } : a,
		),
	};
}

/**
 * Updates the title of a sachverhalt in the detailed klausur data.
 */
export function updateSachverhaltTitleInData(
	data: KorrekturStudentKlausurDTO,
	sachverhaltId: string,
	title: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) =>
				s.id === sachverhaltId ? { ...s, title } : s,
			),
		})),
	};
}

/**
 * Updates the title of a sachverhalt in the navigation structure.
 */
export function updateSachverhaltTitleInNav(
	nav: NavigationKlausurDTO,
	sachverhaltId: string,
	title: string,
): NavigationKlausurDTO {
	return {
		...nav,
		aufgabenstellungen: nav.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) =>
				s.id === sachverhaltId ? { ...s, title } : s,
			),
		})),
	};
}

/**
 * Updates the title of a musterloesung in the detailed klausur data.
 */
export function updateMusterloesungTitleInData(
	data: KorrekturStudentKlausurDTO,
	musterloesungId: string,
	title: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) => ({
				...s,
				musterloesungen: (s.musterloesungen ?? []).map((m) =>
					(m.korrekturMusterloesungId || m.id) === musterloesungId
						? { ...m, title }
						: m,
				),
			})),
		})),
	};
}

/**
 * Removes an aufgabenstellung from the detailed klausur data.
 */
export function removeAufgabenstellungFromData(
	data: KorrekturStudentKlausurDTO,
	aufgabenstellungId: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.filter(
			(a) => a.id !== aufgabenstellungId,
		),
	};
}

/**
 * Removes an aufgabenstellung from the navigation structure.
 */
export function removeAufgabenstellungFromNav(
	nav: NavigationKlausurDTO,
	aufgabenstellungId: string,
): NavigationKlausurDTO {
	return {
		...nav,
		aufgabenstellungen: nav.aufgabenstellungen.filter(
			(a) => a.id !== aufgabenstellungId,
		),
	};
}

/**
 * Removes a sachverhalt from the detailed klausur data.
 */
export function removeSachverhaltFromData(
	data: KorrekturStudentKlausurDTO,
	aufgabenstellungId: string,
	sachverhaltId: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) =>
			a.id === aufgabenstellungId
				? {
						...a,
						sachverhalte: a.sachverhalte.filter((s) => s.id !== sachverhaltId),
					}
				: a,
		),
	};
}

/**
 * Removes a sachverhalt from the navigation structure.
 */
export function removeSachverhaltFromNav(
	nav: NavigationKlausurDTO,
	aufgabenstellungId: string,
	sachverhaltId: string,
): NavigationKlausurDTO {
	return {
		...nav,
		aufgabenstellungen: nav.aufgabenstellungen.map((a) =>
			a.id === aufgabenstellungId
				? {
						...a,
						sachverhalte: a.sachverhalte.filter((s) => s.id !== sachverhaltId),
					}
				: a,
		),
	};
}

/**
 * Adds a new musterloesung to a sachverhalt in the detailed klausur data.
 */
export function addMusterloesungToData(
	data: KorrekturStudentKlausurDTO,
	sachverhaltId: string,
	musterloesung: MusterloesungDTO,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) => {
				if (s.id !== sachverhaltId) return s;
				const nextMls = (s.musterloesungen ?? []).concat(musterloesung);
				return { ...s, musterloesungen: nextMls };
			}),
		})),
	};
}

/**
 * Adds a new korrekturbogen item to a musterloesung in the detailed klausur data.
 */
export function addKorrekturbogenItemToData(
	data: KorrekturStudentKlausurDTO,
	musterloesungId: string,
	item: KorrekturKorrekturbogenItem,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) => ({
				...s,
				musterloesungen: (s.musterloesungen ?? []).map((m) => {
					if ((m.korrekturMusterloesungId || m.id) !== musterloesungId)
						return m;
					const nextItems = (m.korrekturbogenItems ?? []).concat(item);
					return { ...m, korrekturbogenItems: nextItems };
				}),
			})),
		})),
	};
}

/**
 * Updates the max points of a korrekturbogen item in the detailed klausur data.
 */
export function updateKorrekturbogenItemMaxPointsInData(
	data: KorrekturStudentKlausurDTO,
	korrekturbogenItemId: string,
	maxPoints: number,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) => ({
				...s,
				musterloesungen: (s.musterloesungen ?? []).map((m) => ({
					...m,
					korrekturbogenItems: (m.korrekturbogenItems ?? []).map((i) =>
						i.id === korrekturbogenItemId ? { ...i, maxPoints } : i,
					),
				})),
			})),
		})),
	};
}

/**
 * Updates the text of a korrekturbogen item in the detailed klausur data.
 */
export function updateKorrekturbogenItemTextInData(
	data: KorrekturStudentKlausurDTO,
	korrekturbogenItemId: string,
	text: string,
): KorrekturStudentKlausurDTO {
	return {
		...data,
		aufgabenstellungen: data.aufgabenstellungen.map((a) => ({
			...a,
			sachverhalte: a.sachverhalte.map((s) => ({
				...s,
				musterloesungen: (s.musterloesungen ?? []).map((m) => ({
					...m,
					korrekturbogenItems: (m.korrekturbogenItems ?? []).map((i) =>
						i.id === korrekturbogenItemId ? { ...i, text } : i,
					),
				})),
			})),
		})),
	};
}

/**
 * Finds a korrekturbogen item by id and returns its text.
 * Returns empty string if not found.
 */
export function findKorrekturbogenItemText(
	data: KorrekturStudentKlausurDTO | null,
	korrekturbogenItemId: string,
): string {
	if (!data) return "";
	for (const a of data.aufgabenstellungen ?? []) {
		for (const s of a.sachverhalte ?? []) {
			for (const m of s.musterloesungen ?? []) {
				const found = (m.korrekturbogenItems ?? []).find(
					(i) => i.id === korrekturbogenItemId,
				);
				if (found) return found.text;
			}
		}
	}
	return "";
}

/**
 * Finds a korrekturbogen item by id and returns its max points.
 * Returns 0 if not found.
 */
export function findKorrekturbogenItemMaxPoints(
	data: KorrekturStudentKlausurDTO | null,
	korrekturbogenItemId: string,
): number {
	if (!data) return 0;
	for (const a of data.aufgabenstellungen ?? []) {
		for (const s of a.sachverhalte ?? []) {
			for (const m of s.musterloesungen ?? []) {
				const found = (m.korrekturbogenItems ?? []).find(
					(i) => i.id === korrekturbogenItemId,
				);
				if (found) return found.maxPoints;
			}
		}
	}
	return 0;
}
